type HeaderObject = { [key: string]: string | null };

/**
 * Merges multiple header objects into a single object.
 * Headers are case-insensitive, so they are all merged as lowercase to prevent duplicating headers with different cases.
 *
 * @param {...Object} headers - Header objects to merge. Can include undefined values.
 * @returns {Object} A new object containing all merged headers.
 *
 * @example
 * const headers1 = { 'Content-Type': 'application/json', 'X-Custom-Header': 'value1' };
 * const headers2 = { 'x-custom-header': 'value2', 'Authorization': 'Bearer token' };
 * const mergedHeaders = mergeHeaders(headers1, headers2);
 * // Result:
 * // {
 * //   'content-type': 'application/json',
 * //   'x-custom-header': 'value2',
 * //   'authorization': 'Bearer token'
 * // }
 */
export function mergeHeaders(...headers: (HeaderObject | undefined)[]): HeaderObject {
    return headers.reduce((result: HeaderObject, headerObj) => {
        if (!headerObj) {
            return result;
        }

        return Object.entries(headerObj).reduce((accResult, [key, value]) => {
            accResult[key.toLowerCase()] = value;

            return accResult;
        }, result);
    }, {});
}
